import { FC, useState } from 'react';
import { MetricValueComparison } from './MetricValueComparison';
import { Button, Stack, Theme, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import LinearProgress from '@mui/material/LinearProgress';
import { Edit } from '@mui/icons-material';
import { useDrawerContext } from '../../../../../drawer/DrawerContext';
import { FormattedMessage } from 'react-intl';
import { metricFormatValue } from '../../../../utils/metricFormatValue';
import { ContentMetric, useContentMetric } from '../../../../useContentMetric';
import { getNightOrDayPalette } from '../../../../../utils/getNightOrDayPalette';

type Props = Readonly<{
	activityId: string;
	contentMetric: ContentMetric;
	compareStart: string;
	compareEnd: string;
	targetStart: string;
	targetEnd: string;
	unitSign: string | null;
}>;

const getTitleColor = (theme: Theme, showHighLight: boolean) => {
	if (getNightOrDayPalette(theme) === 'day') {
		return theme.palette.day[600];
	}
	if (showHighLight) {
		return theme.palette.night[200];
	}

	return theme.palette.night[800];
};

const MetricCellRow: FC<Props> = ({ activityId, contentMetric, compareStart, compareEnd, targetStart, targetEnd, unitSign }) => {
	const theme = useTheme();
	const [showEditButton, setShowEditButton] = useState(false);
	const { setDrawerWithoutPrevParams } = useDrawerContext();
	const [showHighlight, setShowHighlight] = useState(false);

	const { data, isLoading } = useContentMetric({ contentMetric, compareStart, compareEnd, targetStart, targetEnd });

	const handleEditMetric = () => {
		setShowHighlight(true);
		setDrawerWithoutPrevParams('editMetricWidget', { activityId, contentMetricId: contentMetric.id }, () => {
			setShowHighlight(false);
		});
	};

	if (isLoading) {
		return (
			<Stack width={'100%'} height={'1.7em'} justifyContent={'center'}>
				<LinearProgress />
			</Stack>
		);
	}

	return (
		<Stack
			onMouseEnter={() => setShowEditButton(true)}
			onMouseLeave={() => setShowEditButton(false)}
			sx={{
				position: 'relative',
				flexDirection: 'row',
				alignItems: 'center',
				justifyContent: 'space-between',
				gap: 2,
				minHeight: 28,
				paddingX: 1,
				paddingY: 0.5,
				marginX: '-8px',
				border: showHighlight ? `1px solid ${theme.palette.primary[400]}` : 'none',
				borderRadius: 1,
				color: showHighlight ? theme.palette[getNightOrDayPalette(theme)][500] : theme.palette[getNightOrDayPalette(theme)][700],
				backgroundColor: showHighlight ? theme.palette.primary[100] : showEditButton ? theme.palette.grey[100] : 'inherit',
			}}
		>
			<Typography
				sx={{
					'-webkit-line-clamp': '2',
					display: '-webkit-box',
					'-webkit-box-orient': 'vertical',
					wordBreak: 'break-word',
					overflow: 'hidden',
				}}
				title={data?.displayMetric?.name}
				fontWeight={400}
				color={getTitleColor(theme, showHighlight)}
			>
				{data?.displayMetric?.name}
			</Typography>
			<Stack sx={{ textWrap: 'nowrap' }} direction={'row'} alignItems={'center'}>
				{data?.displayMetric?.values.targetValue != null && data.displayMetric.precision != null ? (
					<Typography fontWeight={500}>
						{metricFormatValue(data.displayMetric.values.targetValue, data.displayMetric.precision)} {unitSign}
					</Typography>
				) : (
					<Typography>
						<FormattedMessage id={'common.not-available'} />
					</Typography>
				)}
				{data?.displayMetric != null && <MetricValueComparison metric={data.displayMetric} />}
			</Stack>
			{showEditButton && (
				<Button
					size={'small'}
					variant={'outlined'}
					color={'secondary'}
					sx={{
						position: 'absolute',
						top: '2px',
						right: '2px',
						minWidth: 0,
						width: '24px',
						height: '24px',
						backgroundColor: theme.palette.background.default,
						':hover': {
							backgroundColor: theme.palette.background.default,
						},
					}}
					onClick={handleEditMetric}
				>
					<Edit sx={{ width: 16, height: 16 }} />
				</Button>
			)}
		</Stack>
	);
};

export { MetricCellRow };
